import { useContext, useEffect, useRef } from 'react'
import { classes, stylesheet } from 'typestyle'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { get } from '../../../share/find'
import { withRouter } from '../../../contexts/with-router'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'
import { convertToArray, flat, structureObj } from '../../../share/obj-equal'

/* Context ====================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'

/* Hooks ======================================================================================== */
import useBoolean from '../../../hooks/use-boolean'
import useFetch from '../../../hooks/use-fetch'
import useValue from '../../../hooks/use-value'
import useObject from '../../../hooks/use-object'
import useArray from '../../../hooks/use-array'

/* Components =================================================================================== */
import AppLayout from '../../routes/app/layout'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Form } from '../../../components/form'
import { Line } from '../../../components/line'
import { Snippet } from '../../../modules/layouts/snippet'
import { Table } from '../../../components/table'
import { Text } from '../../../components/text/text.component'
import { renderForm } from '../../../modules/handlers/render-form'

/* Styles ======================================================================================= */
import { hide } from '../../../styles/styleset/hide/hide'
import { mb8 } from '../../../styles/styleset/margin/mb8'
import { p16 } from '../../../styles/styleset/padding/p16'
import { p8 } from '../../../styles/styleset/padding/p8'
import { pb16 } from '../../../styles/styleset/padding/pb16'
import { pl16 } from '../../../styles/styleset/padding/pl16'
import { pr8 } from '../../../styles/styleset/padding/pr8'
import { pt8i } from '../../../styles/styleset/padding/pt8i'

const SEARCH = {
  /**
   * Default query will not affect url search parameters
   */
  default: {
    page: 1,
  },
}

export default addSnippet(
  'TableEdit',
  withRouter((props: any) => {
    const { sync, trigger, match } = props
    const { navigation, retrigger } = useContext(NavigationContext)
    const { parentRowId, groupId, baseId } = useParams()
    const history = useNavigate()
    const location = useLocation()

    let url = props.url
      ? `${process.env.REACT_APP_API_URL}${props.url}`
      : `${process.env.REACT_APP_API_URL}${location.pathname}`

    url = url
      .replace('{{parentId}}', parentRowId || '')
      .replace('{{groupId}}', groupId || '')
      .replace('{{baseId}}', baseId || '')

    const baseUrl = url.replace('/a/', '/n/')

    const cache: any = useRef({})

    const [sort, $sort] = useValue({})
    const [sortTrigger, $sortTrigger] = useValue()

    const [, $loaded] = useBoolean(false)

    const [isSubmitting, $isSubmitting] = useBoolean(false)
    const [isEditing, $isEditing] = useBoolean(false)

    const [searchToggled, $searchToggled] = useBoolean(false)
    const [searchTerms, $searchTerms] = useValue({})
    const [searchTermValue, $searchTermValue] = useValue('')
    const [searchTrigger, $searchTrigger] = useValue()

    const appLayoutContext = useContext(AppLayout.context)
    const { getOffsetTop = () => 0 } = appLayoutContext || ({} as any)
    const [rows, $rows] = useFetch<{
      count: number
      data: any[]
      footer: any[]
      page: number
      totalPage: number
    }>(
      {
        count: 0,
        data: [],
        footer: [],
        page: 1,
        totalPage: 1,
      },
      {
        url,
        search: SEARCH.default,
        location,
        updateGetParams: false,
        isLoading: true,
        ...(() => {
          if (!props.url) {
            return {
              onLocationChange: (props: any) => {
                const { url, encodedParam } = props

                window.history.replaceState(
                  null,
                  '',
                  `${url.replace(
                    process.env.REACT_APP_API_URL,
                    process.env.REACT_APP_WEB_URL,
                  )}?${encodedParam}`,
                )
                // ; (history as any).push({ path: url, search: encodedParam })
              },
            }
          }
          return {}
        })(),
      },
    )
    const [individualBase, $individualBase] = useFetch<{
      base: null
    }>(
      {
        base: null,
      },
      {
        url: baseUrl,
        isLoading: true,
      },
    )

    let headers = get('payload.base.table.headers', individualBase, 'array')
    if (individualBase.payload.base) {
      headers = get('payload.base.table.headers', individualBase, 'array')
    }

    let actions = get('payload.base.table.actions', individualBase, 'array')
    if (individualBase.payload.base) {
      actions = get('payload.base.table.actions', individualBase, 'array')
    }

    let form = get('payload.base.form.fields', individualBase, 'array')
    if (individualBase.payload.base) {
      form = get('payload.base.form.fields', individualBase, 'array')
    }

    let name = get('payload.base.name', individualBase, 'object')
    if (individualBase.payload.base) {
      name = get('payload.base.name', individualBase, 'array')
    }

    let base = get('payload.base', individualBase, 'object')
    if (individualBase.payload.base) {
      base = get('payload.base', individualBase, 'array')
    }

    const filters = get('table.filters', base, 'array')
    const lookups = get('table.lookups', base, 'array')

    const [internalRows, $internalRows] = useObject(rows)
    const [values, $values] = useObject(rows.payload.data)
    const [deleteSet, $deleteSet] = useArray<any>([])
    const [selected, $selected] = useValue({})

    const [internalHeaders, $internalHeaders] = useValue(headers)
    const [defaultSearch, $defaultSearch] = useValue()

    useEffect(() => {
      if (
        !props.url &&
        navigation.payload &&
        navigation.payload.base &&
        Object.keys(navigation.payload.base).length > 0 &&
        rows.mounted
      ) {
        const search: any = {}

        search['sort'] = {
          key: 'order',
          direction: 1,
        }

        let selectedLookups
        if (lookups.length > 0) {
          selectedLookups = lookups
            .filter((a: any) => a.allowFromTable)
            .map((item: any) => {
              return {
                key: item.key,
              }
            })
        }

        const defaultSearch = {
          ...search,
          lookups,
        }

        $rows
          .get({
            location: location,
            search: defaultSearch,
          })
          .then((payload: any) => {
            $loaded.set(true)

            $internalRows.set({ payload })
          })

        if (rows.search.search) {
          const searchTermFlatten = flat(rows.search.search)

          $searchTermValue.set(searchTermFlatten[Object.keys(searchTermFlatten)[0]])
        }

        $defaultSearch.set(defaultSearch)
      }
    }, [navigation.payload, rows.mounted])

    useEffect(() => {
      if (individualBase.mounted && props.url) {
        $individualBase.get()
      }
    }, [individualBase.mounted])

    useEffect(() => {
      if (rows.mounted && individualBase?.payload?.base !== null) {
        const defaultSearch = {
          // ...search,
          sort: {
            key: 'order',
            direction: 1,
          },
          search: {},
          lookups,
        }

        $rows
          .get({
            // location: history.location,
            search: defaultSearch,
          })
          .then((payload: any) => {
            $loaded.set(true)
            $internalRows.set({ payload })
            payload.data = payload.data.sort((a: any, b: any) => {
              return a.order < b.order
            })
            props.$rows?.set(payload)
            sync()
          })

        if (rows.search.search) {
          const searchTermFlatten = flat(rows.search.search)

          $searchTermValue.set(searchTermFlatten[Object.keys(searchTermFlatten)[0]])
        }

        if (rows.search.sort) {
          $sort.set({
            ...filters
              .filter((a: any) => a.type === 'sort')[0]
              .options.filter((a: any) => a.name === rows.search.sort.key)[0],
            direction: rows.search.sort.direction,
          })
        } else if (filters.filter((a: any) => a.type === 'sort')[0]) {
          $sort.set({
            ...filters.filter((a: any) => a.type === 'sort')[0].value,
            initial: true,
          })
        }

        $defaultSearch.set(defaultSearch)
      }
    }, [rows.mounted, individualBase.payload])

    useEffect(() => {
      if (retrigger) {
        $rows.get().then((payload: any) => {
          payload.data = payload.data.sort((a: any, b: any) => {
            return a.order < b.order
          })
          $internalRows.set({ payload })
        })
      }
    }, [retrigger])

    useEffect(() => {
      if (trigger) {
        $rows.get().then((payload: any) => {
          payload.data = payload.data.sort((a: any, b: any) => {
            return a.order < b.order
          })
          $internalRows.set({ payload })
        })
      }
    }, [trigger])

    useEffect(() => {
      if (isEditing) {
        $rows.get().then((payload: any) => {
          payload.data = payload.data.sort((a: any, b: any) => {
            return a.order < b.order
          })
          $internalRows.set({ payload })
        })
      }
    }, [isEditing])

    useEffect(() => {
      let unlisten: any
      if (defaultSearch) {
        // unlisten = history.listen(() => {
        //   if (history.action === 'POP') {
        //     $rows.get({
        //       search: parseSearch(history.location, {}),
        //       updateGetParams: true,
        //     })
        //   }
        // })
      }

      return () => {
        if (unlisten) unlisten()
      }
    }, [defaultSearch, rows.loaded])

    useEffect(() => {
      if (headers.length > 0) {
        let newHeaders = [...headers]

        newHeaders = newHeaders.map((header: any) => {
          return {
            ...header,
            renderHeader: (header: any, renderSortIcon: any) => {
              return (
                <Box>
                  {renderSortIcon}
                  <Text size="sm" semiBold>
                    {header.label}
                  </Text>
                </Box>
              )
            },
            renderColumn: (props: any) => {
              const { column, columnKey, row, rowKey, name, formName } = props
              const field = form.find((a: any) => {
                return a.name === (formName || name)?.replace('body.', '')
              })

              return (
                <Box column alignCenterY baseline="none" fullHeight key={columnKey} fullWidth>
                  <Box row baseline="none" fullWidth>
                    {isEditing && field ? (
                      renderForm({
                        field: {
                          ...field,
                          disabled: isSubmitting,
                        },
                        parentName: `${rowKey}.body`,
                        disableWrap: true,
                        disableSeperation: true,
                        cache,
                      })
                    ) : (
                      <>
                        <Text size="sm" baseline="surface" shrink>
                          {column || '-'}
                        </Text>
                        <Box className={hide}>
                          {renderForm({
                            field: {
                              ...field,
                              disabled: isSubmitting,
                            },
                            parentName: `${rowKey}.body`,
                            disableWrap: true,
                            disableSeperation: true,
                            cache,
                          })}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              )
            },
          }
        })

        $internalHeaders.set(newHeaders)
      }
    }, [headers, rows.search, isEditing])

    useEffect(() => {
      if (sortTrigger) {
        delete rows.search.sort
        $rows
          .get({
            search: {
              ...rows.search,
              sort: { key: 'order', direction: 1 },
            },
            updateGetParams: !sort.initial,
          })
          .then((payload: any) => {
            $internalRows.set({ payload })
          })

        $sortTrigger.set(null)
      }
    }, [sortTrigger])

    useEffect(() => {
      if (searchTrigger) {
        $rows
          .get({
            search: {
              page: 1,
              search: searchTerms,
              sort: { key: 'order', direction: 1 },
            },
            updateGetParams: true,
          })
          .then((payload: any) => {
            payload.data = payload.data.sort((a: any, b: any) => {
              return a.order < b.order
            })
            $internalRows.set({ payload })
          })
        $searchTrigger.set(null)
      }
    }, [searchTerms, searchTrigger])

    const triggerSearch = () => {
      const searchKeys = filters.filter((a: any) => a.type === 'search')[0].keys
      const search: any = {}

      searchKeys.map((key: string) => {
        search[key] = searchTermValue
      })

      $searchTerms.set(search)

      $searchTrigger.set(Date.now())
    }

    const getTableActions = () => {
      if (isEditing) {
        return [
          {
            label: 'Delete',
            type: 'event',
            onClick: (data: any, index: any) => {
              const newInternalRows = {
                payload: { data: [...convertToArray(structureObj(values))] },
              }
              newInternalRows.payload.data.splice(index, 1)

              if (data.id) {
                $deleteSet.push(data as any)
              }

              $internalRows.set(newInternalRows)
            },
          },
        ]
      } else {
        return [...(actions ? actions.filter((a: any) => a.target === 'table-row') : [])]
      }
    }

    const formRef: any = useRef(null)

    return (
      <>
        {Object.keys(individualBase.payload.base || {}).length > 0 && (
          <Snippet>
            <Form
              ref={formRef}
              onChange={(data: any) => {
                $values.set(data)
              }}
              onSubmit={(data: any) => {
                $rows
                  .post({
                    body: {
                      values: data,
                    },
                  })
                  .then(() => {
                    $rows.get().then((payload: any) => {
                      payload.data = payload.data.sort((a: any, b: any) => {
                        return a.order < b.order
                      })
                      $internalRows.set({ payload })
                      $isSubmitting.set(false)
                      $isEditing.set(false)
                      triggerSearch()
                      props.$rows?.set(payload)
                    })
                  })
              }}
              values={isEditing ? internalRows?.payload?.data : undefined}
            >
              <Box baseline="none" row>
                <Box baseline="none" row>
                  <Box
                    className={classes(pl16, pr8)}
                    minHeight={48}
                    baseline="none"
                    spaceBetween
                    alignCenterY
                  >
                    <Box flex alignCenterY>
                      <Text size="xl" baseline="surface" semiBold>
                        {props.title || 'Table'}
                      </Text>
                    </Box>

                    <Box flex baseline="none" alignCenterY>
                      {isEditing ? (
                        <Button
                          label={isSubmitting ? 'Saving...' : 'Save'}
                          size="sm"
                          baseline="primary"
                          onClick={async () => {
                            await formRef?.current?.handleSubmit()
                            $isSubmitting.set(true)
                          }}
                          square
                        />
                      ) : (
                        <Button
                          label={'Add/Edit'}
                          size="sm"
                          baseline="primary"
                          square
                          onClick={() => {
                            $isEditing.toggle()
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Line />
              </Box>
              <Box className={STYLES.containerTable} baseline="surface" rounded>
                <Table
                  $selected={$selected}
                  selected={selected}
                  topOffset={getOffsetTop()}
                  headers={[...internalHeaders]}
                  data={internalRows.payload.data}
                  values={values}
                  onDrop={async (data: any) => {
                    if (!isEditing && !isSubmitting) {
                      $isSubmitting.set(true)
                      await formRef?.current?.handleSubmit(data)
                    } else {
                      $internalRows.set({ payload: { data } })
                      $values.set(data)
                    }
                  }}
                  actions={getTableActions()}
                  refresh={() => {
                    $searchTrigger.set(Date.now())
                  }}
                  isSortable={isEditing}
                  isSubmitting={isSubmitting}
                  isEditing={isEditing}
                  isEditable={false}
                >
                  <Box rounded>
                    {!rows.meta.isLoading && internalRows.payload.data.length === 0 && (
                      <Box row rounded>
                        <Line className={mb8} />
                        <Box className={classes(p16, pt8i, pb16)} flex alignCenter rounded>
                          <Box flex alignCenterY rounded>
                            <Text size="sm" italic>
                              No {name.plural?.toLowerCase()} found.
                            </Text>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Box row>
                    {isEditing && (
                      <>
                        <Line />
                        <Box
                          className={classes(pl16, pr8, p8)}
                          flex
                          fullWidth
                          alignCenterY
                          alignCenterX
                        >
                          <Button
                            label={`Add ${base?.name?.singular}`}
                            baseline="primary"
                            size="sm"
                            onClick={() => {
                              const newInternalRows = {
                                payload: { data: [...convertToArray(structureObj(values))] },
                              }

                              newInternalRows.payload.data.push({
                                [`body.${form[0].name}`]: '',
                              })

                              $internalRows.set(newInternalRows)
                            }}
                            square
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                </Table>
              </Box>
            </Form>
          </Snippet>
        )}
      </>
    )
  }),
)

const STYLES = stylesheet({
  containerTable: {},
  buttonSearch: {
    width: 32,
    // height: '32px !important',
    borderRadius: '4px 0 0 4px',
  },
})
